import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from 'src/contexts/SessionContext';
import { WaitListEntry } from 'src/models/appointment/_types';
import { deleteWaitListEntry } from 'src/models/appointment/appointment';
import { isTrainer } from 'src/models/user/utils';
import { Visit, VisitStatus } from 'src/models/visit/_types';
import { updateVisit } from 'src/models/visit/visit';
import { CheckInBlockerDialogProps } from 'src/shared/CheckinBlocker/CheckInBlockerDialog';
import { CheckinBlockerOverrideContext } from 'src/shared/CheckinBlocker/_types';
import { useCheckInBlocker } from 'src/shared/CheckinBlocker/useCheckInBlocker';
import { Identifier } from 'src/types/utilityTypes';
import toast from 'src/utils/toast';

export const useAppointmentMembersSection = (): {
  updateVisitStatus: (
    visit: Visit,
    status: VisitStatus,
    additionalParams?: {
      chargeNoshow?: boolean;
      chargeCancellation?: boolean;
      paidByPackageId?: boolean;
    },
    overridePaymentBlock?: boolean,
  ) => Promise<void>;
  bulkUpdateVisits: (visits: Visit[], params: Partial<Visit>, overrideCheckInBlocker?: boolean) => Promise<void>;
  deleteWaitlistEntry: (entry: WaitListEntry) => void;
  isUpdatingVisit: boolean;
  checkInBlockerDialogProps: CheckInBlockerDialogProps;
} => {
  const queryClient = useQueryClient();
  const { user } = useSession();
  const { t: tCheckinBlocker } = useTranslation('translation', { keyPrefix: 'checkinBlocker' });

  const pendingVisitsRef = useRef<Visit[]>([]);
  const onCloseRef = useRef<() => void>(() => {});

  const updateVisitMutation = useMutation({
    mutationFn: (visit: Visit) => updateVisit(visit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Visit(s) successfully updated');
    },
    onError: () => {
      toast.error('Visit could not be updated');
    },
  });

  const bulkUpdateVisitMutation = useMutation({
    mutationFn: (visit: Visit) => updateVisit(visit),
  });

  const isUpdating = updateVisitMutation.isPending || bulkUpdateVisitMutation.isPending;

  const getOverrideVisits = (context: CheckinBlockerOverrideContext) => {
    const visits = pendingVisitsRef.current;
    if (context.scope === 'all' || !context.user) return visits;

    return visits.filter(
      (visit) => String(visit.userId) === String(context.user?.id)
    );
  };

  const [checkInBlockerDialogProps, checkBlockers] = useCheckInBlocker({
    title: tCheckinBlocker('actionRequired'),
    onOverride: isTrainer(user)
      ? async (context) => {
          if (isUpdating) return;

          const visits = getOverrideVisits(context);
          if (visits.length === 0) return;

          try {
            if (visits.length > 1) {
              await Promise.all(
                visits.map((v) =>
                  bulkUpdateVisitMutation.mutateAsync({ ...v, status: VisitStatus.Completed }),
                ),
              );
              queryClient.invalidateQueries({ queryKey: ['appointments'] });
              toast.success('Visit(s) successfully updated');
            } else {
              await updateVisitMutation.mutateAsync({
                ...visits[0],
                status: VisitStatus.Completed,
              });
            }
          } catch {
            toast.error('Visit could not be updated');
          }

          if (context.scope === 'all' || !context.user) {
            onCloseRef.current();
          }
        }
      : undefined,
  });

  onCloseRef.current = checkInBlockerDialogProps.onClose;

  const updateVisitStatus = async (
    visit: Visit,
    status: VisitStatus,
    additionalParams?: {
      chargeNoshow?: boolean;
      chargeCancellation?: boolean;
      paidByPackageId?: boolean;
    },
    overridePaymentBlock = false,
  ) => {
    if (status === VisitStatus.Completed && !overridePaymentBlock) {
      pendingVisitsRef.current = [visit];
      const userIds = [visit.userId].filter(Boolean) as Identifier[];
      const blockers = await checkBlockers({ userIds });
      if (blockers.length > 0) return;
    }

    await updateVisitMutation.mutateAsync({ ...visit, status, ...additionalParams });
  };

  const deleteWaitListMutation = useMutation({
    mutationFn: (entry: WaitListEntry) => deleteWaitListEntry({ id: entry.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Successfully removed from waitlist');
    },
    onError: () => {
      toast.error('Unable to remove from waitlist');
    },
  });

  const deleteWaitlistEntry = (entry: WaitListEntry) => {
    deleteWaitListMutation.mutate(entry);
  };

  const bulkUpdateVisits = async (
    visits: Visit[],
    params: Partial<Visit>,
    overrideCheckInBlocker = false,
  ) => {
    if (params.status === VisitStatus.Completed && !overrideCheckInBlocker) {
      pendingVisitsRef.current = visits;
      const userIds = visits.map((v) => v.userId).filter(Boolean) as Identifier[];
      const blockers = await checkBlockers({ userIds });
      if (blockers.length > 0) return;
    }

    try {
      await Promise.all(
        visits.map((visit) => bulkUpdateVisitMutation.mutateAsync({ ...visit, ...params })),
      );
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Visit(s) successfully updated');
    } catch {
      toast.error('Visit could not be updated');
    }
  };

  return {
    updateVisitStatus,
    bulkUpdateVisits,
    deleteWaitlistEntry,
    isUpdatingVisit: updateVisitMutation.isPending,
    checkInBlockerDialogProps,
  };
};
